<div class="flex flex-col h-full">
  <div class="mt-2 h-10 shrink-0 flex items-center justify-between mb-4 md:mb-6 gap-4">
    <h1 class="headline-medium text-primary">{{ 'AI blueprints' | translate }}</h1>
    <button
      mat-flat-button
      color="primary"
      type="button"
      routerLink="/ai-blueprints/new"
      class="shrink-0 max-md:!hidden"
      data-testid="create-ai-blueprint-button">
      <mat-icon svgIcon="plus"/>
      {{ 'Create AI blueprint' | translate }}
    </button>
    <button
      mat-stroked-button
      color="primary"
      type="button"
      routerLink="/ai-blueprints/new"
      class="shrink-0 !h-10 md:!hidden"
      data-testid="create-ai-blueprint-button">
      <mat-icon svgIcon="plus"/>
      {{ 'Create' | translate }}
    </button>
  </div>

  <div class="flex-1 min-h-0 overflow-hidden relative">
    @if (showLoadingSkeleton) {
      <div
        class="h-full overflow-y-auto overscroll-y-contain bg-surface border border-outline-variant rounded-lg md:hidden [-webkit-overflow-scrolling:touch]"
        data-testid="mobile-blueprint-list"
        aria-hidden="true">
        <div
          class="grid grid-cols-[minmax(0,1fr)_auto] items-center sticky top-0 z-10 bg-surface-container-lowest border-b border-outline-variant title-small text-on-surface-variant py-4.5 pr-3 pl-2"
          data-testid="mobile-blueprint-list-header">
          <span>{{ 'Document' | translate }}</span>
          <span class="pr-1">{{ 'Default' | translate }}</span>
        </div>
        <div class="bg-white">
          @for (row of [0, 1, 2, 3, 4, 5]; track row) {
            <div
              class="grid grid-cols-[minmax(0,1fr)_auto] items-center pl-1 min-h-22 border-b border-outline-variant"
              data-testid="mobile-blueprint-row">
              <div class="min-w-0 grid grid-cols-[minmax(0,1fr)] items-center py-3 pl-1 cursor-default">
                <div class="flex flex-col gap-1">
                  <div class="rounded-md animate-pulse bg-surface-container-highest w-[min(42vw,180px)] h-4.5"></div>
                  <div class="rounded-md animate-pulse bg-surface-container-highest w-[min(46vw,200px)] h-3.25"></div>
                </div>
              </div>
              <div class="animate-pulse bg-surface-container-highest rounded-full m-3 w-6 h-6"></div>
            </div>
          }
        </div>
      </div>

      <div class="hidden md:block h-full">
        <app-ai-blueprint-skeleton [count]="6"></app-ai-blueprint-skeleton>
      </div>
    } @else if (isLoading) {
      <div class="h-full"></div>
    } @else if (hasError) {
      <div class="empty-state">
        <div class="empty-state-header"></div>
        <div class="empty-state-body">
          <p class="empty-state-title text-primary">{{ 'Unable to load AI blueprints' | translate }}</p>
          <p class="empty-state-subtitle text-on-surface-variant">{{ 'Please try again later' | translate }}</p>
        </div>
      </div>
    } @else if (dataSource.data.length === 0) {
      <div class="empty-state">
        <div class="empty-state-header"></div>
        <div class="empty-state-body">
          <p class="empty-state-title text-primary">{{ 'No AI blueprints created yet' | translate }}</p>
          <p class="empty-state-subtitle text-on-surface-variant">{{ 'Once added, your AI blueprints will appear here' | translate }}</p>
        </div>
      </div>
    } @else {
      <div
        #mobileListWrapper
        class="h-full overflow-y-auto overscroll-y-contain bg-surface border border-outline-variant rounded-lg md:hidden [-webkit-overflow-scrolling:touch]"
        data-testid="mobile-blueprint-list"
        (scroll)="onMobileListScroll()">
        <div
          class="grid grid-cols-[minmax(0,1fr)_auto] items-center sticky top-0 z-10 bg-surface-container-lowest border-b border-outline-variant title-small text-on-surface-variant py-3 pr-3 pl-2"
          data-testid="mobile-blueprint-list-header">
          <span>{{ 'Document' | translate }}</span>
          <span class="pr-1">{{ 'Default' | translate }}</span>
        </div>
        <div class="bg-white">
          @for (blueprint of dataSource.data; track blueprint.id) {
            <article
              class="grid grid-cols-[minmax(0,1fr)_auto] items-center pl-2 min-h-22 border-b border-outline-variant"
              data-testid="mobile-blueprint-row">
              <button
                type="button"
                class="min-w-0 grid grid-cols-[minmax(0,1fr)] items-center border-0 bg-transparent text-left cursor-pointer py-3 pl-1 [-webkit-tap-highlight-color:transparent] focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 focus-visible:rounded-sm"
                (click)="openBlueprint(blueprint)"
                [attr.aria-label]="blueprint.name"
                data-testid="ai-blueprint-mobile-row">
                <div>
                  <span class="body-large text-primary truncate block w-full">{{ blueprint.name }}</span>
                  <div class="title-small flex flex-col items-start flex-wrap gap-1 min-w-0 text-on-surface-variant">
                    @if (blueprint.createdAt) {
                      <span>{{ 'Created' | translate }} {{ blueprint.createdAt | date:'dd/MM/yyyy' }}</span>
                    }
                    @if (blueprint.updatedAt) {
                      <span>{{ 'Updated' | translate }} {{ blueprint.updatedAt | date:'dd/MM/yyyy' }}</span>
                    }
                  </div>
                </div>
              </button>

              <div class="self-center pr-3">
                <mat-slide-toggle
                  [checked]="blueprint.isDefault"
                  (click)="onToggleDefault(blueprint, $event)"
                  data-testid="ai-blueprint-mobile-default-toggle">
                </mat-slide-toggle>
              </div>
            </article>
          }
        </div>

        @if (showLoadingMoreSkeleton) {
          <div class="bg-white" aria-hidden="true">
            @for (row of [0, 1, 2]; track row) {
              <div
                class="grid grid-cols-[minmax(0,1fr)_auto] items-center pl-1 min-h-22 border-b border-outline-variant"
                data-testid="mobile-blueprint-row">
                <div class="min-w-0 grid grid-cols-[minmax(0,1fr)] items-center py-3 pl-1 cursor-default">
                  <div class="flex flex-col gap-1">
                    <div class="rounded-md animate-pulse bg-surface-container-highest w-[min(42vw,180px)] h-4.5"></div>
                    <div class="rounded-md animate-pulse bg-surface-container-highest w-[min(46vw,200px)] h-3.25"></div>
                  </div>
                </div>
                <div class="animate-pulse bg-surface-container-highest rounded-full m-3 w-6 h-6"></div>
              </div>
            }
          </div>
        }
      </div>

      <div
        #tableWrapper
        class="table-wrapper hidden md:block bg-white border rounded-lg w-full max-h-full overflow-auto relative border-outline-variant"
        (scroll)="onTableScroll()">
        <table mat-table [dataSource]="dataSource" class="min-w-[600px] w-full">

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>{{ 'Name' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cell-emphasis truncate max-w-[400px]" [title]="element.name">
              {{ element.name }}
            </td>
          </ng-container>

          <ng-container matColumnDef="created">
            <th mat-header-cell *matHeaderCellDef>{{ 'Created' | translate }}</th>
            <td mat-cell *matCellDef="let element">{{ (element.createdAt | date:'dd/MM/yyyy') ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="updated">
            <th mat-header-cell *matHeaderCellDef>{{ 'Updated' | translate }}</th>
            <td mat-cell *matCellDef="let element">{{ (element.updatedAt | date:'dd/MM/yyyy') ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="default">
            <th mat-header-cell *matHeaderCellDef>{{ 'Default' | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <mat-slide-toggle
                [checked]="element.isDefault"
                (click)="onToggleDefault(element, $event)"
                data-testid="ai-blueprint-default-toggle">
              </mat-slide-toggle>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns;"
            class="cursor-pointer"
            (click)="openBlueprint(row)"
            data-testid="ai-blueprint-row"></tr>
        </table>

        @if (showLoadingMoreSkeleton) {
          <app-ai-blueprint-skeleton [count]="3" [showHeader]="false"></app-ai-blueprint-skeleton>
        }
      </div>
    }
  </div>
</div>
